<template lang="pug">
.form-fields
  .fields-row(v-for="(row, rowIndex) in preparedScheme", :key="getRowKey(row, rowIndex)")
    .field(v-for="item in row", :key="`${schemaVersion}:${item.field.name}`", :style="{ width: `${item.width}%` }")
      component(
        :is="getFieldComponent(item.field)",
        v-bind="getFieldProps(item.field)",
        @change="value => changeField(item.field, value)",
        @initial-load="initialLoad(item.field.name)"
      )
</template>

<script setup>
import { computed } from "vue";
import fieldsComponents from "../../general_components/fields/index.js";
import { useStore } from "@/store";

const props = defineProps({
  fields: { type: Array, default: () => [] },
  scheme: { type: Array, default: () => [] },
  values: { type: Object, default: () => ({}) },
  watchValues: { type: Object, default: () => ({}) },
  invalidFields: { type: Object, default: () => ({}) },
  schemaVersion: { type: Number, default: 0 },
  formLoaded: { type: Boolean, default: true },
  watchRequest: { type: Function, default: null },
  watchParentsImmediate: { type: Boolean, default: false },
});

const emit = defineEmits(["change", "initial-load"]);
const store = useStore();

const fieldAliases = {
  datetime: "date",
  editor: "text",
  info: "text",
};

const fieldByName = name => props.fields.find(field => field.name === name);

const fieldWidth = item => {
  if (item.width !== undefined && item.width !== null) return item.width;
  if (item.col !== undefined && item.col !== null) return (Number(item.col) / 12) * 100;

  return 100;
};

const preparedScheme = computed(() =>
  props.scheme
    .map(row =>
      row
        .map(item => ({ ...item, field: fieldByName(item.name), width: fieldWidth(item) }))
        .filter(
          item =>
            Boolean(item.field) &&
            item.field.visible !== false &&
            (item.field.type !== "info" || Boolean(props.values[item.field.name])),
        ),
    )
    .filter(row => row.length > 0),
);

const getRowKey = (row, rowIndex) => `${rowIndex}:${row.map(item => item.field.name).join("_")}`;

const getFieldComponent = field => fieldsComponents[fieldAliases[field.type] || field.type];

const getFullOptionsPath = field => {
  if (field.use_inline_options) return "";
  if (field.full_options_path) return field.full_options_path;
  if (!field.options_path) return "";
  if (typeof field.options_path === "string" && field.options_path.startsWith("/")) return field.options_path;

  return store.state.paths[field.options_path] || "";
};

const normalizedWatchParents = field => {
  if (field.watch?.parent) return [field.watch.parent];

  return (field.watch?.parents || []).map(parent => parent?.parent).filter(parent => parent);
};

const getWatch = field => {
  const [parent] = normalizedWatchParents(field);

  return parent ? { name: parent, value: props.watchValues[parent] } : null;
};

const getMultiwatch = field =>
  normalizedWatchParents(field)
    .slice(1)
    .map(parent => ({ name: parent, value: props.watchValues[parent] }));

const getFieldProps = field => {
  const disabled = Boolean(field.disabled || field.readonly || field.type === "info");
  const value = props.values[field.name];
  const normalizedType = fieldAliases[field.type] || field.type;
  const fieldWatch = field.watch || null;

  return {
    itsFormField: true,
    field,
    name: field.name,
    type: field.type,
    multiple: Boolean(field.multiple),
    label: field.label || field.title || "",
    placeholder: field.placeholder || "",
    hint: field.hint || field.instruction || null,
    optionsParams: field.options_params || {},
    fullOptionsPath: getFullOptionsPath(field),
    options: field.options || field.data || [],
    // selectField historically expects { name, value }, while the other form fields
    // receive the backend watch contract unchanged.
    watch: normalizedType === "select" ? getWatch(field) : fieldWatch,
    multiwatch: getMultiwatch(field),
    watchValues: props.watchValues,
    ...(normalizedType === "date"
      ? { watchRequest: props.watchRequest, watchParentsImmediate: props.watchParentsImmediate }
      : {}),
    readonly: disabled,
    disable: disabled,
    initialValue: value,
    value,
    required: Boolean(field.require ?? field.required),
    invalid: hasInvalidField(field.name),
    errorMessage: props.invalidFields[field.name] || "",
    formLoaded: props.formLoaded,
    autogrow: Boolean(field.autogrow),
    withWrapper: normalizedType === "date",
    minDate: field.min_date || "",
    maxDate: field.max_date || "",
    showLabel: Boolean(field.show_label),
    title: field.checkbox_title || "",
    accept: field.accept || "",
  };
};

const hasInvalidField = name => Object.prototype.hasOwnProperty.call(props.invalidFields, name);

const changeField = (field, value) => {
  emit("change", { name: field.name, field, value });
};

const initialLoad = name => {
  emit("initial-load", name);
};
</script>

<style scoped lang="scss">
.form-fields {
  .fields-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .field {
    min-width: 0;
    padding-bottom: 10px;
  }
}
</style>
